import { useState } from 'react'
import { useApp } from '../context/AppContext'
import { sileo } from 'sileo'
import { useNavigate, Link } from 'react-router-dom'
import LogoTicketsBlanco from '../assets/logo-tickets-blanco.svg'
import LogoTickets from '../assets/logo-tickets.svg'

/**
 * Pantalla de ingreso.
 *
 * Es la misma de todos los sistemas internos (Design System → `starter/login.php`):
 * panel de marca en navy a la izquierda y tarjeta de acceso a la derecha. Las
 * clases (.login-page, .login-sidebar, .login-card, .btn-login…) salen de
 * styles/gm-app-shell.css, así que se ve igual que Proyectos u Operaciones.
 *
 * @component
 * @returns {JSX.Element}
 */
const Login = () => {
  const navigate = useNavigate()
  const { login } = useApp()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  /**
   * Convierte el error de Supabase Auth en un mensaje en español, conservando
   * los datos técnicos (código/estado) para soporte.
   * @param {*} err
   * @returns {string}
   */
  const describeAuthError = (err) => {
    if (!err) return 'Error desconocido al iniciar sesión.'

    const msg = err.message || String(err)
    const status = err.status ?? err.statusCode
    const code = err.code || err.name
    const lower = msg.toLowerCase()

    let friendly
    if (lower.includes('invalid login credentials')) {
      friendly = 'Correo o contraseña incorrectos.'
    } else if (lower.includes('email not confirmed')) {
      friendly =
        'Tu correo aún no ha sido confirmado. Revisá tu bandeja de entrada.'
    } else if (lower.includes('user not found')) {
      friendly = 'No existe una cuenta asociada a ese correo.'
    } else if (status === 429 || lower.includes('rate limit')) {
      friendly = 'Demasiados intentos. Esperá unos minutos e intentá de nuevo.'
    } else if (lower.includes('failed to fetch') || lower.includes('network')) {
      friendly =
        'No se pudo conectar con el servidor. Revisá tu conexión a internet.'
    } else if (status >= 500) {
      friendly =
        'El servidor de autenticación tuvo un problema. Intentá más tarde.'
    } else {
      friendly = msg
    }

    const details = [code && `código: ${code}`, status && `estado: ${status}`]
      .filter(Boolean)
      .join(' · ')

    return details ? `${friendly} (${details})` : friendly
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await login(email, password)
      sileo.success('Bienvenido')
      navigate('/')
    } catch (err) {
      console.error('Error al iniciar sesión:', err)
      setError(describeAuthError(err))
    }

    setLoading(false)
  }

  return (
    <div className="login-page">
      {/* Panel de marca (se oculta en móvil) */}
      <aside className="login-sidebar">
        <div className="logo-container">
          <img src={LogoTicketsBlanco} alt="Sistema de Tickets de Grupo Mecsa" />
          <h4>Sistema de Tickets</h4>
          <p>Acceso restringido a colaboradores autorizados.</p>
        </div>
      </aside>

      {/* Tarjeta de acceso */}
      <main className="login-content">
        <form className="login-card" onSubmit={handleLogin} noValidate>
          <div className="login-header">
            <img
              src={LogoTickets}
              alt=""
              className="login-mark d-lg-none"
              width="56"
              height="56"
            />
            <h2>Bienvenido</h2>
            <p>Ingresá con tu correo corporativo</p>
          </div>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          <div className="form-floating mb-3">
            <input
              type="email"
              className="form-control"
              id="email"
              placeholder="correo"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <label htmlFor="email">Correo corporativo</label>
          </div>

          <div className="form-floating mb-3">
            <input
              type="password"
              className="form-control"
              id="password"
              placeholder="contraseña"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <label htmlFor="password">Contraseña</label>
          </div>

          <button type="submit" className="btn-login w-100" disabled={loading}>
            {loading ? 'Entrando…' : 'Entrar'}
          </button>

          <div className="login-links">
            {/* La misma página de todas las apps: código de 6 dígitos al correo. */}
            <a
              href="https://ops.grupomecsa.net/restablecer.php?app=Tickets&volver=https%3A%2F%2Ftickets.grupomecsa.net%2F"
              className="forgot-password"
            >
              ¿Olvidaste tu contraseña?
            </a>
            <Link to="/ayuda" className="forgot-password">
              Centro de ayuda
            </Link>
          </div>
        </form>
      </main>
    </div>
  )
}

export default Login
